import React, { useEffect, useMemo, useState } from 'react';
import { UserProfile, Role, Tenant, Board, UserStatus } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import { CollapsibleSection } from '../CollapsibleSection';
import { BulkUserUpload } from './BulkUserUpload';
import { parseCsvToUsers } from '../../utils/csvUtils';
import { Dropdown } from '../Dropdown';
import { FileUpload } from '../FileUpload';
import { BOARDS } from '../../constants';
import { ReportingView } from './ReportingView';
import { useAuth } from '../../contexts/AuthContext';

type AdminTab = 'create' | 'configure' | 'reporting' | 'user_management';
const PartnerUserManagement: React.FC<{
  partnerId: string;
  tenantId: string;
  refreshNonce: number;
}> = ({ partnerId, tenantId, refreshNonce }) => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [invites, setInvites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [userList, inviteList] = await Promise.all([
        firestoreService.getUsersForPartnerTenant(partnerId, tenantId),
        firestoreService.getInvitationsForPartnerTenant(partnerId, tenantId),
      ]);

      setUsers((userList || []).sort((a, b) => String(a.email || '').localeCompare(String(b.email || ''))));

      setInvites(
        (inviteList || [])
          .filter((inv: any) => String(inv?.status || '').toUpperCase().trim() === 'PENDING')
          .sort((a: any, b: any) => String(a.email || '').localeCompare(String(b.email || '')))
      );
    } catch (e) {
      console.error('[PartnerUserManagement] load failed', e);
      alert('Failed to load users/invitations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!partnerId || !tenantId) return;
    fetchUsers();
  }, [partnerId, tenantId, refreshNonce]);

  const handleStatusToggle = async (user: UserProfile) => {
    const newStatus = user.status === UserStatus.ACTIVE ? UserStatus.INACTIVE : UserStatus.ACTIVE;
    try {
      await firestoreService.updateUserStatus(user.uid, newStatus);
      setUsers(prev => prev.map(u => (u.uid === user.uid ? { ...u, status: newStatus } : u)));
    } catch (e) {
      console.error('[PartnerUserManagement] status update failed', e);
      alert('Failed to update user status.');
    }
  };

  const handleRevokeInvite = async (inviteIdRaw: string) => {
    const inviteId = String(inviteIdRaw || '').trim();
    if (!inviteId) return;

    try {
      await firestoreService.revokeInvitationByEmail(inviteId);
      setInvites(prev => prev.filter((inv: any) => String(inv.id || '').trim() !== inviteId));
    } catch (e) {
      console.error('[PartnerUserManagement] revoke failed', e);
      alert('Failed to revoke invitation.');
    }
  };

  if (loading) return <p>Loading users...</p>;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="overflow-x-auto bg-white rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(u => (
                <tr key={u.uid}>
                  <td className="px-4 py-2 text-sm">{u.email}</td>
                  <td className="px-4 py-2 text-sm">{u.role}</td>
                  <td className="px-4 py-2 text-sm">
                    <span
                      className={`px-2 inline-flex text-xs font-semibold rounded-full ${
                        u.status === UserStatus.ACTIVE ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {u.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm text-center">
                    <button onClick={() => handleStatusToggle(u)} className="text-brand-orange hover:underline text-xs">
                      {u.status === UserStatus.ACTIVE ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}

              {users.length === 0 && (
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-500" colSpan={4}>
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <button onClick={fetchUsers} className="text-xs text-gray-500 hover:underline">
          Refresh
        </button>
      </div>

      <div className="space-y-2">
        <div className="text-sm font-semibold text-brand-grey">Pending Invitations</div>
        <div className="overflow-x-auto bg-white rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invites.map((inv: any) => (
                <tr key={inv.id || inv.inviteId || inv.email}>
                  <td className="px-4 py-2 text-sm">{inv.email}</td>
                  <td className="px-4 py-2 text-sm">{inv.role}</td>
                  <td className="px-4 py-2 text-sm">{inv.status}</td>
                  <td className="px-4 py-2 text-sm text-center">
                    <button onClick={() => handleRevokeInvite(inv.id)} className="text-brand-orange hover:underline text-xs">
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}

              {invites.length === 0 && (
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-500" colSpan={4}>
                    No pending invitations.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
export const PartnerAdminView: React.FC<{
  userProfile: UserProfile;
  tenant: Tenant;
}> = ({ userProfile, tenant }) => {
  const [activeTab, setActiveTab] = useState<AdminTab>('create');

  // Auth-scoped partner tenants (single source of truth)
  const { partnerTenantList } = useAuth();

 const managedTenants = useMemo(() => {
  const list = Array.isArray(partnerTenantList) ? partnerTenantList : [];

  const mapped = list
    .map((t: any) => {
      const id = String(t?.id || t?.tenantId || t?.tenant_id || '').trim();
      const name = String(t?.tenantName || t?.name || t?.tenant_name || '').trim();
      return { id, name: name || id };
    })
    .filter((x: any) => !!x.id);

  mapped.sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)));
  return mapped;
}, [partnerTenantList]);

  const managedTenantIds = useMemo(
    () => new Set(managedTenants.map(t => t.id)),
    [managedTenants]
  );

  // Create User Form State
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<Role>(Role.TEACHER_USER);
  const [newUserTenantId, setNewUserTenantId] = useState('');
const [newUserDisplayName, setNewUserDisplayName] = useState('');
// Bulk Upload – Target Tenant (single-tenant deterministic bulk)
const [bulkUploadTenantId, setBulkUploadTenantId] = useState<string>('');
  // Configure Tab – Target Tenant (MUST be here)
  const [configureTenantId, setConfigureTenantId] = useState<string>('');
  // Partner Admin: User Management – Target Tenant
  const [userMgmtTenantId, setUserMgmtTenantId] = useState<string>('');
  const [userMgmtRefreshNonce, setUserMgmtRefreshNonce] = useState(0);
  const requestUserMgmtRefresh = () => setUserMgmtRefreshNonce(n => n + 1);
useEffect(() => {
  if (activeTab === 'user_management') {
    requestUserMgmtRefresh();
  }
}, [activeTab]);
  // Branding state
  const [tenantLogoUrl, setTenantLogoUrl] = useState<string>('');
  const [tenantLogoFile, setTenantLogoFile] = useState<File | null>(null);
  const [savingLogo, setSavingLogo] = useState<boolean>(false);
  const [brandingFeedback, setBrandingFeedback] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Learning Outcomes State
  const [loFile, setLoFile] = useState<File | null>(null);
  const [loBoard, setLoBoard] = useState<Board>(Board.CBSE);
  const [loTitle, setLoTitle] = useState('');
  const [loScope, setLoScope] = useState('K-12');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formFeedback, setFormFeedback] = useState<{
    key: string;
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const showFormFeedback = (
    key: string,
    text: string,
    type: 'success' | 'error' = 'success',
    duration = 5000
  ) => {
    setFormFeedback({ key, type, text });
    setTimeout(
      () => setFormFeedback(prev => (prev?.key === key ? null : prev)),
      duration
    );
  };

  // Default configure tenant
  useEffect(() => {
    if (!configureTenantId && managedTenants.length > 0) {
      setConfigureTenantId(managedTenants[0].id);
    }
  }, [managedTenants, configureTenantId]);
useEffect(() => {
  if (!bulkUploadTenantId && managedTenants.length > 0) {
    setBulkUploadTenantId(managedTenants[0].id);
  }
}, [managedTenants, bulkUploadTenantId]);
useEffect(() => {
  if (!newUserTenantId && managedTenants.length > 0) {
    setNewUserTenantId(managedTenants[0].id);
  }
}, [managedTenants, newUserTenantId]);
  // When configure tenant changes, hydrate branding fields from auth list
  useEffect(() => {
    const selected = (partnerTenantList || []).find(
      (t: any) => t.id === configureTenantId
    );
    setTenantLogoUrl((selected as any)?.tenantLogoUrl || '');
    setBrandingFeedback(null);
    setTenantLogoFile(null);
  }, [configureTenantId, partnerTenantList]);
useEffect(() => {
  if (!userMgmtTenantId && managedTenants.length > 0) {
    setUserMgmtTenantId(managedTenants[0].id);
  }
}, [managedTenants, userMgmtTenantId]);
  // Debug log
  // eslint-disable-next-line no-console
  console.log(
    '[PartnerAdminView] partnerTenantList:',
    partnerTenantList?.length,
    partnerTenantList
  );

  const renderFeedback = (key: string) =>
    formFeedback?.key === key ? (
      <div
        className={`my-4 p-3 rounded-md text-sm ${
          formFeedback.type === 'error'
            ? 'bg-red-100 text-red-800'
            : 'bg-green-100 text-green-800'
        }`}
      >
        {formFeedback.text}
      </div>
    ) : null;

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
 try {
  await firestoreService.createUser(
    {
      email: newUserEmail,
      role: newUserRole,
      tenantId: newUserTenantId,
      partnerId: userProfile.partnerId,
      displayName: newUserDisplayName,
    },
    userProfile
  );

requestUserMgmtRefresh();
setActiveTab('user_management');
setUserMgmtTenantId(String(newUserTenantId || '').trim());

  showFormFeedback('createUser', `User invitation for "${newUserEmail}" sent.`);
  setNewUserEmail('');
  setNewUserTenantId('');
  setNewUserRole(Role.TEACHER_USER);
  setNewUserDisplayName('');
} catch (error: any) {
  showFormFeedback('createUser', error?.message || 'Failed to create user.', 'error');
}finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadOutcomes = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!configureTenantId) {
      showFormFeedback('lo', 'Please select a tenant to configure.', 'error');
      return;
    }

    if (!loFile || !loTitle.trim() || !loScope.trim()) {
      showFormFeedback('lo', 'Please provide a Title, Scope, and select a PDF file.', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
     // File -> base64 (strip data URL prefix)
const base64File: string = await new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('lo_file_read_failed'));
  reader.onload = () => {
    const result = String(reader.result || '');
    const commaIdx = result.indexOf(',');
    resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
  };
  reader.readAsDataURL(loFile);
});

// Backend-only LO parsing
const runResult: any = await runService.startRun(
  tenant,
  userProfile,
  'lo_parse',
  {
    board: loBoard,
    loFile: {
      content: base64File,
      type: loFile.type || 'application/pdf',
    },
  }
);

const items =
  Array.isArray(runResult?.items) ? runResult.items :
  Array.isArray(runResult?.aiResult?.items) ? runResult.aiResult.items :
  [];


      const timestamp = new Date();
      const generatedDocId = `${String(loBoard)
        .toLowerCase()
        .replace(/\s/g, '-')}_${loScope.toLowerCase().replace(/\s/g, '-')}_${timestamp.getTime()}`;
      const generatedVersion = timestamp.toISOString();

      await firestoreService.uploadLearningOutcomesForTenant(
        configureTenantId,
        loBoard,
        {
          doc_id: generatedDocId,
          version: generatedVersion,
          title: loTitle,
          scope: loScope,
        },
        items
      );

      showFormFeedback('lo', `Uploaded ${items.length} outcomes for ${loBoard}.`, 'success');
      setLoFile(null);
      setLoTitle('');
      setLoScope('K-12');
    } catch (error: any) {
      showFormFeedback('lo', error?.message || 'Failed to upload outcomes.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const processPartnerUsers = async (file: File) => {
  const expectedHeaders = ['email', 'role'];
  const users = await parseCsvToUsers(file, expectedHeaders);

  const partnerId = String(userProfile.partnerId || '').trim();
  if (!partnerId) throw new Error('Partner context is missing.');

  const tenantId = String(bulkUploadTenantId || '').trim();
  if (!tenantId) throw new Error('Select a tenant first for bulk upload.');
  if (!managedTenantIds.has(tenantId)) {
    throw new Error('Selected tenant is not managed by this partner.');
  }

  const tenantName =
    managedTenants.find(t => t.id === tenantId)?.name
      ? String(managedTenants.find(t => t.id === tenantId)!.name)
      : null;

  const createdByUid = String((userProfile as any)?.uid || '').trim();
  if (!createdByUid) throw new Error('missing_createdByUid');

  const rowErrors: any[] = [];
  const rows: Array<{ email: string; role: Role }> = [];

  for (const user of users) {
    const rowNumber = (user as any)?.rowNumber;
    const email = String((user as any)?.email || '').trim().toLowerCase();
    const role = String((user as any)?.role || '').trim().toUpperCase() as Role;

    if (!email) {
      rowErrors.push({ rowNumber, email, role, error: 'row_missing_email' });
      continue;
    }
    if (!email.includes('@')) {
      rowErrors.push({ rowNumber, email, role, error: 'row_invalid_email' });
      continue;
    }
    if (role !== Role.TENANT_ADMIN && role !== Role.TEACHER_USER) {
      rowErrors.push({ rowNumber, email, role, error: 'role_not_allowed_for_bulk' });
      continue;
    }

    rows.push({ email, role });
  }

  rows.sort((a, b) => `${a.email}__${a.role}`.localeCompare(`${b.email}__${b.role}`));

  if (!rows.length) return { created: 0, skipped: 0, errors: rowErrors };

  const res = await firestoreService.bulkAddUsers({
    createdByUid,
    tenantId,
    tenantName,
    partnerId,
    rows,
  });
requestUserMgmtRefresh();
setActiveTab('user_management');
setUserMgmtTenantId(String(bulkUploadTenantId || '').trim());
return { ...res, errors: [...rowErrors, ...(res?.errors || [])] };
};
const renderUserManagementTab = () => (
  <div className="space-y-6">
    <div className="bg-white p-4 rounded-lg border">
      <label className="block text-sm font-semibold text-brand-grey mb-2">User Management Target Tenant</label>

      <select
        value={userMgmtTenantId}
        onChange={e => setUserMgmtTenantId(e.target.value)}
        className="w-full border px-3 py-2 rounded-md"
      >
        {managedTenants.map(t => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
    </div>

    <PartnerUserManagement
      partnerId={String(userProfile.partnerId || '').trim()}
      tenantId={String(userMgmtTenantId || '').trim()}
      refreshNonce={userMgmtRefreshNonce}
    />
  </div>
);
  const renderCreateTab = () => (
    <div className="space-y-6">
   
      <CollapsibleSection title="Managed Tenants" defaultOpen={true}>
        <div className="p-4">
          {managedTenants.length > 0 ? (
            <ul className="list-disc list-inside space-y-1 text-sm">
              {managedTenants.map(t => (
                <li key={t.id}>{t.name}</li>
              ))}
            </ul>
          ) : (
            <p>No tenants assigned.</p>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Create Single User">
        <form onSubmit={handleCreateUser} className="space-y-4 p-4">
          {renderFeedback('createUser')}

          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">Email</label>
            <input
              type="email"
              value={newUserEmail}
              onChange={e => setNewUserEmail(e.target.value)}
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
              required
            />
          </div>
<div>
  <label className="block text-sm font-bold text-brand-grey mb-1">Display Name</label>
  <input
    type="text"
    value={newUserDisplayName}
    onChange={e => setNewUserDisplayName(e.target.value)}
    className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
    required
  />
</div>
          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">Role</label>
            <select
              value={newUserRole}
              onChange={e => setNewUserRole(e.target.value as Role)}
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
            >
              <option value={Role.TEACHER_USER}>Teacher</option>
              <option value={Role.TENANT_ADMIN}>Tenant Admin</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-brand-grey mb-1">Assign to Tenant</label>
            <select
              value={newUserTenantId}
              onChange={e => setNewUserTenantId(e.target.value)}
              className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
              required
            >
              <option value="">Select Tenant</option>
              {managedTenants.map(t => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-brand-orange text-white font-semibold rounded-md shadow-md disabled:bg-gray-400"
            >
              Create User
            </button>
          </div>
        </form>
      </CollapsibleSection>

      <CollapsibleSection title="Bulk User Upload">
        <div className="p-4 pt-0">
  <label className="block text-sm font-bold text-brand-grey mb-1">Bulk Upload Target Tenant</label>
  <select
    value={bulkUploadTenantId}
    onChange={e => setBulkUploadTenantId(e.target.value)}
    className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md"
    required
  >
    {managedTenants.map(t => (
      <option key={t.id} value={t.id}>
        {t.name}
      </option>
    ))}
  </select>
</div>
<BulkUserUpload
  templateHeaders={['email', 'role']}
  templateFilename="partner_user_template.csv"
  processAndUpload={processPartnerUsers}
/>
      </CollapsibleSection>
    </div>
  );

  const renderConfigureTab = () => (
    <div className="space-y-6">
      {/* Configure Tenant Selector */}
      <div className="bg-white p-4 rounded-lg border">
        <label className="block text-sm font-semibold text-brand-grey mb-2">Configure Tenant</label>

        <select
          value={configureTenantId}
          onChange={e => setConfigureTenantId(e.target.value)}
          className="w-full border px-3 py-2 rounded-md"
        >
          {managedTenants.map(t => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      {/* Tenant Branding */}
      <CollapsibleSection title="Tenant Branding: Logo" defaultOpen={true}>
        <div className="p-4 space-y-3">
          <label className="block text-sm font-bold text-brand-grey mb-1">Upload Tenant Logo</label>

          <input
            type="file"
            accept="image/*"
            className="w-full border px-3 py-2 rounded-md"
            disabled={!configureTenantId || savingLogo}
            onChange={e => {
              const f = e.target.files?.[0] || null;
              setTenantLogoFile(f);
              setBrandingFeedback(null);
            }}
          />

          {tenantLogoUrl ? (
            <div className="text-xs text-gray-600 break-all">Current URL: {tenantLogoUrl}</div>
          ) : null}

          {brandingFeedback ? (
            <div
              className={`text-sm ${
                brandingFeedback.type === 'success' ? 'text-green-700' : 'text-red-700'
              }`}
            >
              {brandingFeedback.text}
            </div>
          ) : null}

          <div className="flex justify-end">
            <button
              type="button"
              className="px-4 py-2 bg-brand-orange text-white text-sm font-semibold rounded-md shadow-sm hover:bg-opacity-90 disabled:bg-gray-300"
              disabled={!configureTenantId || savingLogo}
              onClick={async () => {
                if (!configureTenantId) return;

                setSavingLogo(true);
                setBrandingFeedback(null);

                try {
                  if (!tenantLogoFile) {
                    throw new Error('Select an image file first.');
                  }

                  const url = await firestoreService.uploadTenantLogoFile(
                    configureTenantId,
                    tenantLogoFile
                  );

                  await firestoreService.updateTenantLogoUrl(configureTenantId, url);

                  setTenantLogoUrl(url);
                  setBrandingFeedback({ type: 'success', text: 'Tenant logo uploaded and saved.' });
                } catch (e: any) {
                  setBrandingFeedback({
                    type: 'error',
                    text: e?.message || 'Failed to upload tenant logo.',
                  });
                } finally {
                  setSavingLogo(false);
                }
              }}
            >
              Save Logo
            </button>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );

  return (
    <>
      <div className="mb-6 flex border-b-2 border-gray-200">
{(['create', 'configure', 'reporting', 'user_management'] as AdminTab[]).map(tab => (          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-2 text-lg font-semibold capitalize transition-colors ${
              activeTab === tab
                ? 'border-b-2 border-brand-orange text-brand-orange'
                : 'text-gray-500 hover:text-brand-grey'
            }`}
          >
{tab === 'user_management' ? 'User Management' : tab}          </button>
        ))}
      </div>

     {activeTab === 'create' && renderCreateTab()}
{activeTab === 'configure' && renderConfigureTab()}
{activeTab === 'reporting' && <ReportingView userProfile={userProfile} />}
{activeTab === 'user_management' && renderUserManagementTab()}
    </>
  );
};
